
import React, { useEffect, useState } from 'react';
import { Loader2, Terminal, Cpu, Database, Network } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useKai } from '../contexts/KaiPresenceContext';
import { KaiLogo } from './KaiLogo';

interface Props {
  title?: string;
  status?: string;
  message?: string;
  variant?: 'card' | 'fullscreen';
}

const BOOT_SEQUENCE = [
    { text: "INITIALIZING KAI_CORE_V4...", delay: 0 },
    { text: "ESTABLISHING NEURAL HANDSHAKE", delay: 800 },
    { text: "LOADING PERSONALITY ENGRAMS...", delay: 1500 },
    { text: "DECRYPTING MEMORY SHARDS", delay: 2400 },
    { text: "CALIBRATING EMPATHY ENGINE", delay: 3200 },
    { text: "SYNCHRONIZING WITH CLOUD ATLAS", delay: 4000 },
    { text: "SYSTEM ONLINE. WAKE UP.", delay: 4800 },
];

export const LoadingProgressBar: React.FC<Props> = ({ 
  title = "Loading Knowledge Base", 
  status = "Fetching Docs", 
  message = "Retrieving architectural diagrams and system specifications...",
  variant = 'card'
}) => {
  const [progress, setProgress] = useState(0);
  const [logLine, setLogLine] = useState(0);
  const { theme } = useTheme();
  
  // Dynamic Theme Integration
  const { state: kaiState } = useKai();
  const color = kaiState.color;
  
  // Secondary accent mapping for rich gradients
  const secondaryMap: Record<string, string> = {
      indigo: 'purple',
      rose: 'fuchsia',
      emerald: 'teal',
      amber: 'orange'
  };
  const secondary = secondaryMap[color] || 'purple';

  useEffect(() => {
    // 1. Progress Timer (Slightly randomized for organic feel)
    const duration = 5000; // 5s boot time
    const intervalTime = 50;
    const steps = duration / intervalTime;
    let currentStep = 0;

    const timer = setInterval(() => {
      currentStep++;
      const rawProgress = (currentStep / steps) * 100;
      
      // Add randomness/hiccups
      const jitter = Math.random() * 5 - 2.5; 
      const newProgress = Math.min(100, Math.max(0, rawProgress + jitter));
      
      setProgress(newProgress);

      if (currentStep >= steps) {
        clearInterval(timer);
        setProgress(100);
      }
    }, intervalTime);

    return () => clearInterval(timer);
  }, []);

  // 2. Log Line Cycler based on progress thresholds
  useEffect(() => {
      const totalLogs = BOOT_SEQUENCE.length;
      const index = Math.floor((progress / 100) * totalLogs);
      setLogLine(Math.min(index, totalLogs - 1));
  }, [progress]);

  // --- CARD VARIANT (Simple) ---
  if (variant === 'card') {
      return (
        <div className={`flex flex-col items-center justify-center p-8 animate-fade-in transition-colors duration-300 ${theme.components.card} h-full w-full`}>
          <div className="w-full max-w-sm space-y-6 text-center">
            <div className="flex justify-center mb-4">
                 <div className={`w-12 h-12 rounded-xl flex items-center justify-center animate-spin-slow ${theme.mode === 'dark' ? `bg-${color}-900/30 text-${color}-400` : `bg-${color}-50 text-${color}-600`}`}>
                    <Loader2 size={24} />
                 </div>
            </div>
            <div className="space-y-2">
                <h3 className={`text-lg font-bold ${theme.colors.text.main}`}>{title}</h3>
                <div className={`flex justify-between text-xs font-mono uppercase tracking-wider ${theme.colors.text.secondary}`}>
                    <span>{status}</span>
                    <span>{Math.round(progress)}%</span>
                </div>
                <div className={`h-1.5 rounded-full overflow-hidden w-full ${theme.mode === 'dark' ? 'bg-slate-800' : 'bg-gray-100'}`}>
                    <div 
                        className={`h-full bg-gradient-to-r from-${color}-500 to-${secondary}-500 transition-all duration-200 ease-out rounded-full`}
                        style={{ width: `${progress}%` }}
                    ></div>
                </div>
            </div>
            <p className={`text-xs max-w-xs mx-auto pt-2 ${theme.colors.text.secondary}`}>
                {message}
            </p>
          </div>
        </div>
      );
  }

  // --- FULLSCREEN VARIANT ("The Genesis") ---
  return (
    <div className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-[#05050A] text-white overflow-hidden perspective-1000">
      
      {/* 1. Matrix Background */}
      <div className="absolute inset-0 opacity-20 pointer-events-none">
          <div className={`absolute top-1/4 left-1/4 w-96 h-96 bg-${color}-600 rounded-full mix-blend-screen filter blur-[120px] animate-pulse-slow`}></div>
          <div className={`absolute bottom-1/4 right-1/4 w-64 h-64 bg-${secondary}-600 rounded-full mix-blend-screen filter blur-[100px] animate-pulse-slow`} style={{ animationDelay: '1s' }}></div>
          {/* Subtle Grid */}
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:40px_40px] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_50%,#000_70%,transparent_100%)]"></div>
      </div>

      {/* 2. Central Monolith (The Interface) */}
      <div className="relative z-10 flex flex-col items-center gap-16 w-full max-w-md px-6">
          
          {/* The Entity (Logo Crystallization) */}
          <div className="relative">
              {/* Energy Ring */}
              <div 
                className={`absolute inset-[-40px] rounded-full border border-${color}-500/20`}
                style={{ 
                    transform: `scale(${1 + (progress / 100) * 0.5}) rotate(${progress * 2}deg)`,
                    opacity: 1 - (progress / 100)
                }}
              ></div>
              
              {/* Blur Halo that sharpens */}
              <div 
                className={`absolute inset-0 bg-${color}-500 rounded-full blur-2xl transition-all duration-300`}
                style={{ 
                    opacity: 0.5 - (progress / 200), 
                    transform: `scale(${1.5 - (progress / 200)})` 
                }}
              ></div>

              {/* The Logo itself */}
              <div 
                className="transition-all duration-1000 cubic-bezier(0.22, 1, 0.36, 1)"
                style={{ 
                    filter: `blur(${(100 - progress) / 5}px) contrast(${100 + progress}%)`,
                    transform: `scale(${0.8 + (progress / 300)})`,
                    opacity: progress / 20
                }}
              >
                  <KaiLogo size={120} animated={true} color={color} />
              </div>
          </div>

          {/* 3. System Diagnostics (Terminal) */}
          <div className="w-full space-y-4">
              {/* Progress Bar */}
              <div className="relative h-1 w-full bg-slate-800 rounded-full overflow-hidden">
                  <div 
                    className={`absolute top-0 left-0 h-full bg-gradient-to-r from-${color}-500 via-${secondary}-500 to-white shadow-[0_0_10px_white] transition-all duration-200 ease-out`}
                    style={{ width: `${progress}%` }}
                  ></div>
              </div>

              {/* Terminal Output */}
              <div className="h-24 font-mono text-xs md:text-sm flex flex-col justify-end space-y-1">
                  <div className={`flex items-center gap-2 text-${color}-400 opacity-50 mb-2`}>
                      <Terminal size={12} />
                      <span className="uppercase tracking-widest text-[10px]">System Log</span>
                  </div>
                  
                  {/* Previous Log (Fading out) */}
                  {logLine > 0 && (
                      <p className="text-slate-500 opacity-40 translate-y-2 transition-all">
                          {'>'} {BOOT_SEQUENCE[logLine - 1].text} ... <span className="text-green-900">OK</span>
                      </p>
                  )}
                  
                  {/* Active Log (Typing/Bright) */}
                  <p className={`text-${color}-200 animate-pulse font-bold tracking-wide`}>
                      {'>'} {BOOT_SEQUENCE[logLine].text} <span className="animate-blink">_</span>
                  </p>
              </div>

              {/* Stats Row */}
              <div className="flex justify-between border-t border-white/10 pt-4 mt-2">
                  <div className="flex items-center gap-2 text-slate-500 text-[10px] uppercase font-bold">
                      <Cpu size={12} />
                      <span>Neural Core: Active</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-500 text-[10px] uppercase font-bold">
                      <Network size={12} />
                      <span>Latency: 12ms</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-500 text-[10px] uppercase font-bold">
                      <Database size={12} />
                      <span>Mem: {Math.round(progress * 10.24)}MB</span>
                  </div>
              </div>
          </div>

      </div>
    </div>
  );
};
